type ColorSchemeOption = {
    value: string;
    label: string;
};

type ColorSchemeGroup = {
    label: string;
    options: ColorSchemeOption[];
};

export const COMPONENT_COLOR_SCHEME_GROUPS: ColorSchemeGroup[] = [
    {
        label: 'Neutrals',
        options: [
            { value: 'white', label: 'White Primary' },
            { value: 'white-secondary', label: 'White Secondary' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
        ],
    },
    {
        label: 'Primary Colors',
        options: [
            { value: 'light-primary', label: 'Light Primary' },
            { value: 'mid-primary', label: 'Mid Primary' },
            { value: 'primary', label: 'Primary' },
            { value: 'dark-primary', label: 'Dark Primary' },
            { value: 'darker-primary', label: 'Darker Primary' },
        ],
    },
    {
        label: 'Secondary Colors',
        options: [
            { value: 'light-secondary', label: 'Light Secondary' },
            { value: 'mid-secondary', label: 'Mid Secondary' },
            { value: 'secondary', label: 'Secondary' },
            { value: 'dark-secondary', label: 'Dark Secondary' },
            { value: 'darker-secondary', label: 'Darker Secondary' },
        ],
    },
    {
        label: 'Gradients',
        options: [
            { value: 'light-gradient', label: 'Light Gradient' },
            { value: 'mid-gradient', label: 'Mid Gradient' },
            { value: 'gradient', label: 'Gradient' },
            { value: 'dark-gradient', label: 'Dark Gradient' },

            {
                value: 'light-primary-gradient',
                label: 'Light Primary Gradient',
            },
            { value: 'primary-gradient', label: 'Primary Gradient' },
            { value: 'dark-primary-gradient', label: 'Dark Primary Gradient' },
            {
                value: 'light-secondary-gradient',
                label: 'Light Secondary Gradient',
            },
            { value: 'secondary-gradient', label: 'Secondary Gradient' },
            {
                value: 'dark-secondary-gradient',
                label: 'Dark Secondary Gradient',
            },
        ],
    },
    {
        label: 'Box Effects',
        options: [
            { value: 'box-primary', label: 'Box Primary' },
            { value: 'box-border-primary', label: 'Box Border Primary' },
            { value: 'box-secondary', label: 'Box Secondary' },
            { value: 'box-border-secondary', label: 'Box Border Secondary' },
        ],
    },
    {
        label: 'Effects',
        options: [
            { value: 'clear', label: 'Clear' },
            { value: 'no-filter', label: 'No Filter' },
            { value: 'no-filter-dark', label: 'No Filter Dark' },
            { value: 'blur', label: 'Blur' },
            { value: 'blur-primary', label: 'Blur Primary' },
            { value: 'blur-secondary', label: 'Blur Secondary' },
            { value: 'dark-blur', label: 'Dark Blur' },
        ],
    },
    {
        label: 'Transparent',
        options: [
            { value: 'transparent-dark', label: 'Transparent Dark' },
            { value: 'transparent-light', label: 'Transparent Light' },
            { value: 'transparent-primary', label: 'Transparent Primary' },
            { value: 'transparent-secondary', label: 'Transparent Secondary' },
        ],
    },
    {
        label: 'Float',
        options: [
            { value: 'float', label: 'Float' },
            { value: 'float-dark', label: 'Float Dark' },
            { value: 'float-blur', label: 'Float Blur' },
            { value: 'float-blur-dark', label: 'Float Blur Dark' },
        ],
    },
];

export function componentSchemeToSwatch(value: string): string {
    switch (value) {
        case 'white':
            return 'bg-white ring-1 ring-primary-700';
        case 'clear':
            return 'bg-white ring-1 ring-black/5';

        case 'white-secondary':
            return 'bg-white ring-1 ring-secondary-700';
        case 'white-primary':
            return 'bg-white ring-1 ring-primary-700';
        case 'light':
            return 'bg-neutral-100';
        case 'dark':
            return 'bg-neutral-900';

        case 'primary':
            return 'bg-primary-700';
        case 'light-primary':
            return 'bg-primary-200';
        case 'mid-primary':
            return 'bg-primary-400';
        case 'dark-primary':
            return 'bg-primary-900';
        case 'darker-primary':
            return 'bg-primary-950';
        case 'box-primary':
            return 'bg-primary-700';
        case 'box-border-primary':
            return 'bg-white ring-1 ring-primary-700';

        case 'secondary':
            return 'bg-secondary-700';
        case 'light-secondary':
            return 'bg-secondary-200';
        case 'mid-secondary':
            return 'bg-secondary-400';
        case 'dark-secondary':
            return 'bg-secondary-900';
        case 'darker-secondary':
            return 'bg-secondary-950';
        case 'box-secondary':
            return 'bg-secondary-700';
        case 'box-border-secondary':
            return 'bg-white ring-1 ring-secondary-700';

        case 'light-gradient':
            return 'bg-gradient-to-r from-primary-200 to-secondary-200';
        case 'mid-gradient':
            return 'bg-gradient-to-r from-primary-400 to-secondary-400';
        case 'gradient':
            return 'bg-gradient-to-r from-primary-600 to-secondary-800';
        case 'dark-gradient':
            return 'bg-gradient-to-r from-primary-800 to-secondary-800';
        case 'light-primary-gradient':
            return 'bg-gradient-to-r from-primary-200 to-primary-400';
        case 'primary-gradient':
            return 'bg-gradient-to-r from-primary-500 to-primary-700';
        case 'dark-primary-gradient':
            return 'bg-gradient-to-r from-primary-800 to-primary-900';
        case 'light-secondary-gradient':
            return 'bg-gradient-to-r from-secondary-200 to-secondary-400';
        case 'secondary-gradient':
            return 'bg-gradient-to-r from-secondary-500 to-secondary-700';
        case 'dark-secondary-gradient':
            return 'bg-gradient-to-r from-secondary-800 to-secondary-900';

        case 'no-filter':
            return 'bg-neutral-200';
        case 'no-filter-dark':
            return 'bg-neutral-700';
        case 'blur':
            return 'backdrop-blur-sm bg-neutral-200/70';
        case 'blur-primary':
            return 'backdrop-blur-sm bg-blur-primary-swatch';
        case 'blur-secondary':
            return 'backdrop-blur-sm bg-blur-secondary-swatch';
        case 'dark-blur':
            return 'backdrop-blur-sm bg-neutral-800/70';
        case 'light-overflow':
            return 'bg-white';
        case 'dark-overflow':
            return 'bg-neutral-800';
        case 'transparent-dark':
        case 'transparent-dark-bigger':
            return 'bg-gradient-to-b from-neutral-900/60 to-neutral-900/80 border border-white/10';
        case 'transparent-light':
        case 'transparent-light-bigger':
            return 'bg-white/70 border border-neutral-200';
        case 'transparent-primary':
        case 'transparent-primary-bigger':
            return 'bg-primary-800/70 border border-primary-400/50';
        case 'transparent-secondary':
        case 'transparent-secondary-bigger':
            return 'bg-secondary-800/70 border border-secondary-400/50';
        case 'float':
            return 'bg-white shadow-md ring-1 ring-neutral-200';
        case 'float-dark':
            return 'bg-neutral-900 shadow-md ring-1 ring-neutral-700';
        case 'float-blur':
            return 'backdrop-blur bg-white/70 ring-1 ring-neutral-200';
        case 'float-blur-dark':
            return 'backdrop-blur bg-neutral-900/70 ring-1 ring-neutral-700';
        default:
            return 'bg-neutral-200';
    }
}

export function normalizeComponentColorScheme(
    value: string,
    groups: ColorSchemeGroup[] = COMPONENT_COLOR_SCHEME_GROUPS
): string {
    const flat = groups.flatMap((g) => g.options || []);
    const values = flat.map((o) => o.value);
    return values.includes(value) ? value : values[0] || 'light';
}

// ----- Card-only Color Schemes -----
export const CARD_COLOR_SCHEME_GROUPS: ColorSchemeGroup[] = [
    {
        label: 'Neutrals',
        options: [
            { value: 'white-primary', label: 'White Primary ' },
            { value: 'white-secondary', label: 'White Secondary' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
        ],
    },
    {
        label: 'Primary Colors',
        options: [
            { value: 'light-primary', label: 'Light Primary' },
            { value: 'mid-primary', label: 'Mid Primary' },
            { value: 'primary', label: 'Primary' },
            { value: 'dark-primary', label: 'Dark Primary' },
            { value: 'darker-primary', label: 'Darker Primary' },
        ],
    },
    {
        label: 'Secondary Colors',
        options: [
            { value: 'light-secondary', label: 'Light Secondary' },
            { value: 'mid-secondary', label: 'Mid Secondary' },
            { value: 'secondary', label: 'Secondary' },
            { value: 'dark-secondary', label: 'Dark Secondary' },
            { value: 'darker-secondary', label: 'Darker Secondary' },
        ],
    },
    {
        label: 'Gradients',
        options: [
            { value: 'light-gradient', label: 'Light Gradient' },
            { value: 'mid-gradient', label: 'Mid Gradient' },
            { value: 'gradient', label: 'Gradient' },
            { value: 'dark-gradient', label: 'Dark Gradient' },
            {
                value: 'light-primary-gradient',
                label: 'Light Primary Gradient',
            },
            { value: 'primary-gradient', label: 'Primary Gradient' },
            {
                value: 'dark-primary-gradient',
                label: 'Dark Primary Gradient',
            },
            {
                value: 'light-secondary-gradient',
                label: 'Light Secondary Gradient',
            },
            { value: 'secondary-gradient', label: 'Secondary Gradient' },
            {
                value: 'dark-secondary-gradient',
                label: 'Dark Secondary Gradient',
            },
        ],
    },
    {
        label: 'Box Effects',
        options: [
            { value: 'box-border-primary', label: 'Box Border Primary' },
            { value: 'box-border-secondary', label: 'Box Border Secondary' },
        ],
    },
];

export const CARD_COLOR_SCHEME_OPTIONS: ColorSchemeOption[] =
    CARD_COLOR_SCHEME_GROUPS.flatMap((group) => group.options || []);

export function getCardColorSchemeOptions(): ColorSchemeOption[] {
    return CARD_COLOR_SCHEME_OPTIONS;
}

export function normalizeCardColorScheme(
    value: string,
    options: ColorSchemeOption[] = CARD_COLOR_SCHEME_OPTIONS
): string {
    const values = options.map((o) => o.value);
    return values.includes(value) ? value : values[0] || 'light';
}

// ----- Map Color Schemes  -----
export const MAP_COLOR_SCHEME_GROUPS: ColorSchemeGroup[] = [
    {
        label: 'Neutrals',
        options: [
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
            { value: 'full-color', label: 'Full Color' },
        ],
    },
    {
        label: 'Primary Colors',
        options: [
            { value: 'light-primary', label: 'Light Primary' },
            { value: 'mid-primary', label: 'Mid Primary' },
            { value: 'primary', label: 'Primary' },
            { value: 'dark-primary', label: 'Dark Primary' },
        ],
    },
    {
        label: 'Secondary Colors',
        options: [
            { value: 'light-secondary', label: 'Light Secondary' },
            { value: 'mid-secondary', label: 'Mid Secondary' },
            { value: 'secondary', label: 'Secondary' },
            { value: 'dark-secondary', label: 'Dark Secondary' },
        ],
    },
    {
        label: 'Gradients',
        options: [
            { value: 'light-gradient', label: 'Light Gradient' },
            { value: 'mid-gradient', label: 'Mid Gradient' },
            { value: 'gradient', label: 'Gradient' },
            { value: 'primary-gradient', label: 'Primary Gradient' },
            { value: 'secondary-gradient', label: 'Secondary Gradient' },
        ],
    },
];

export function mapSchemeToSwatch(value: string): string {
    switch (value) {
        case 'light':
            return 'bg-neutral-200';
        case 'dark':
            return 'bg-neutral-900';
        case 'primary':
            return 'bg-primary-800';
        case 'mid-primary':
            return 'bg-primary-500';
        case 'dark-primary':
            return 'bg-primary-950';
        case 'light-primary':
            return 'bg-primary-200';
        case 'secondary':
            return 'bg-secondary-800';
        case 'mid-secondary':
            return 'bg-secondary-500';
        case 'dark-secondary':
            return 'bg-secondary-950';
        case 'light-secondary':
            return 'bg-secondary-200';
        case 'gradient':
            return 'bg-gradient-to-r from-primary-800 to-secondary-800';
        case 'light-gradient':
            return 'bg-gradient-to-r from-primary-200 to-secondary-200';
        case 'mid-gradient':
            return 'bg-gradient-to-r from-primary-500 to-secondary-500';
        case 'primary-gradient':
            return 'bg-gradient-to-r from-primary-500 to-primary-800';
        case 'secondary-gradient':
            return 'bg-gradient-to-r from-secondary-500 to-secondary-800';
        case 'dark-gradient':
            return 'bg-gradient-to-r from-primary-950 to-secondary-950';
        case 'full-color':
            return 'bg-secondary-500';
        default:
            return 'bg-neutral-200';
    }
}

export function normalizeMapColorScheme(
    value: string,
    groups: ColorSchemeGroup[] = MAP_COLOR_SCHEME_GROUPS
): string {
    const flat = groups.flatMap((g) => g.options || []);
    const values = flat.map((o) => o.value);
    return values.includes(value) ? value : values[0] || 'light';
}
